import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  Building2,
  MapPinIcon,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { callLoader } from "@/redux/Loader/actions";
import { validateForm } from "../../components/ui/useFormValidation";
import { showToast } from "@/redux/Toast/actions";
import { useDebounced } from "@/hooks/useDebounced";
import {
  filterTypeOptions,
  getLocationColumns,
  initialForm,
  validationConfig,
} from "./helper";
import Swal from "sweetalert2";
import moment from "moment";
import Select from "react-select";
import AddModal from "./AddModal";
import Pagination from "../PortPlace/Pagination";
import {
  createLocation,
  DeleteLocation,
  getLocationList,
  UpdatecreateLocation,
} from "@/redux/Masters/location/actions";
import { DataTable } from "@/components/ui/table";

const MaintainLocations = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const dispatch = useDispatch();
  const authUser = JSON.parse(localStorage.getItem("authUser"))?.[0];
  const [formData, setFormData] = useState(initialForm);
  const [formDataErr, setFormDataErr] = useState({});
  const [searchText, setSearchText] = useState("");
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [filterType, setFilterType] = useState("AREA_NAME");
  const searchRef = useRef("");
  const { locationList, pagination } = useSelector((state) => state.locations);
  const { countryList } = useSelector((state) => state.common);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormDataErr((prev) => ({ ...prev, [name]: "" }));
  };

  useEffect(() => {
    _getList(1);
     setCurrentPage(1);
  }, [recordsPerPage]);

  const handlePrevious = () => {
    if (currentPage > 1) _getList(currentPage - 1);
    setCurrentPage(currentPage - 1);
  };

  const handleNext = () => {
    if (pagination?.nextPage > 0) _getList(pagination.nextPage);
    setCurrentPage(pagination.nextPage);
  };

  const _getList = async (page = 1) => {
    dispatch(callLoader(1)); // optional loader

    try {
      const payload = {
        IPageNo: page,
        IPageRecords: recordsPerPage,
        SearchKeyword: encodeURIComponent(searchRef.current),
        FilterType: encodeURIComponent(filterType),
      };

      await dispatch(getLocationList(payload));
    } catch (error) {
      console.error("Error fetching address book list:", error);
      dispatch(showToast("Error fetching locations list", "error"));
    } finally {
      dispatch(callLoader(0));
    }
  };

  const debouncedFetchList = useDebounced(_getList, 300);

  const handleSearch = (value) => {
    setSearchText(value);
    searchRef.current = value;
    debouncedFetchList(1);
    setCurrentPage(1);
  };

  const handleAddNew = () => {
    setFormData(initialForm);
    setOpenModal(true);
  };

  const handleSave = async () => {
    const validationErrors = validateForm({
      data: formData,
      items: [],
      rules: validationConfig,
    });
    setFormDataErr(validationErrors || {});
    if (Object.keys(validationErrors).length > 0) {
      return;
    }
    try {
      dispatch(callLoader(1));
      const currentDateTime = moment()
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
      const payload = {
        ...formData,
      };

      if (Number(formData.id) === 0) {
        payload.createdDate = currentDateTime;
        payload.modifiedDate = currentDateTime;
        const addDetails = await createLocation(payload);
        if (addDetails?.statuscode === 201 || addDetails?.statuscode === 200) {
          dispatch(showToast("Record Save successfully.", "success", true));
          _getList(currentPage);
        } else if (addDetails?.statuscode === 404) {
          dispatch(callLoader(0));
          dispatch(showToast("Locations already exists", "error"));
        } else {
          dispatch(callLoader(0));
        }

        setTimeout(() => {
          setOpenModal(false);
        }, 500);
      } else {
        payload.modifiedDate = currentDateTime;
        const addDetails = await UpdatecreateLocation(payload);
        if (addDetails?.statuscode === 201 || addDetails?.statuscode === 200) {
          dispatch(showToast("Record Update successfully", "success", true));
          _getList(currentPage);
          setTimeout(() => {
            setOpenModal(false);
          }, 500);
        }
        if (addDetails?.statuscode === 404) {
          dispatch(callLoader(0));
          dispatch(showToast("Locations already exists", "error"));
        } else {
          dispatch(callLoader(0));
        }
        //   setOpenModal(false);
      }
    } catch (err) {
      console.log("errerrerr", err);
      dispatch(showToast("Failed to save Locations", "error"));
      dispatch(callLoader(0));
    } finally {
    }
  };

  // Edit Detail
  const handleEdit = (row) => {
    setFormData({
      ...row,
      modifiedByUser: String(authUser.id),
      createdByUser: String(authUser.id),
    });
    setOpenModal(true);
  };
  const handleDelete = async (payload) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This record will be deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
    });

    if (!result.isConfirmed) return;

    const updateDetails = await DeleteLocation({ id: payload?.id });
    if (
      updateDetails?.statuscode === 201 ||
      updateDetails?.statuscode === 200
    ) {
      dispatch(showToast("Record delete successfully.", "success"));
      _getList(1);
    }
  };

  const startIndex = (pagination.currentPage - 1) * recordsPerPage + 1;

  const endIndex = Math.min(
    pagination.currentPage * recordsPerPage,
    pagination.totalRecord,
  );
  const options = (countryList || [])?.map((loc) => {
    const match = (loc.incoterm || "").match(/\(([^)]+)\)/);
    const code = match ? match[1] : null;
    return {
      value: code,
      label: loc.incoterm,
    };
  });

  const handlers = {
    handleEdit,
    handleDelete,
  };

  const columns = getLocationColumns(handlers);

    const handleGoto = (page) => {
    _getList(page);
    setCurrentPage(page);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6"
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-cyan-100 dark:bg-cyan-900/30 rounded-xl flex items-center justify-center">
              <MapPinIcon className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                Locations List
              </h1>
              {/* <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                {pagination?.totalRecord} total port/place
              </p> */}
              {pagination?.totalRecord ? (
                <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  Showing {startIndex}–{endIndex} of {pagination?.totalRecord}{" "}
                  Locations
                </div>
              ) : null}
            </div>
          </div>
          <button
            onClick={() => handleAddNew()}
            data-testid="add-clause-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Locations
          </button>
        </div>

        {/* Search Bar */}

        <div className="bg-white flex items-center space-x-2 dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 mb-6">
          <select
            value={filterType}
            onChange={(e) => {
              if (searchText) {
                handleSearch("");
              }
              setSearchText("");
              setFilterType(e.target.value);
            }}
            className="px-3 py-2 text-sm border-0 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
          >
            {filterTypeOptions &&
              filterTypeOptions.map((value, i) => (
                <option key={i} value={value?.value}>
                  {value?.label}
                </option>
              ))}
          </select>
          {filterType === "COUNTRY" ? (
            <div className="relative w-full max-w-md">
              <Select
                options={options}
                value={options?.find(
                  (opt) => opt.label === formData?.searchText,
                )}
                onChange={(selected) => handleSearch(selected?.value)}
                placeholder="Select Country"
                classNames={{
                  control: (state) =>
                    `min-h-[38px] rounded-md border  bg-white dark:bg-slate-800 text-slate-900 dark:text-white 
                       ${state.isFocused ? "border-[#0072FF] ring-1 ring-[#0072FF]" : "border-slate-300 dark:border-slate-700"}`,
                  menu: () =>
                    "bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700",
                  option: ({ isFocused, isSelected }) =>
                    `cursor-pointer px-3 py-2   ${isSelected ? "bg-[#0072FF] text-white" : ""}
                        ${isFocused && !isSelected ? "bg-slate-100 dark:bg-slate-700" : ""}  text-slate-900 dark:text-white`,

                  singleValue: () => "text-slate-900 dark:text-white",
                  placeholder: () => "text-slate-400 dark:text-slate-500",
                }}
              />
            </div>
          ) : (
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder={`Search by ${filterType !== "LOC_CODE" ? "location name" : "location code"}...`}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
              />
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
          <DataTable
            data={locationList || []}
            columns={columns}
            trClass="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30"
            thClass="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider"
            tdClass="px-4 py-4 text-sm text-slate-600 dark:text-slate-400"
            htrClass="bg-gradient-to-r from-slate-700 to-slate-800 text-white"
            loading={false}
            Icon={Building2}
            emptyText="No Locations found"
            isAplyingFilter={!!searchText}
            resetFilters={() => {
              setSearchText("");
              handleSearch("");
              setFilterType("AREA_NAME");
            }}
          />
        </div>

        {/* Pagination */}
        {locationList.length > 0 && (
          <Pagination
            currentPage={currentPage}
            nextPage={pagination?.nextPage}
            recordsPerPage={recordsPerPage}
            setRecordsPerPage={setRecordsPerPage}
            onPrevious={handlePrevious}
            onNext={handleNext}
            totalRecord={pagination?.totalRecord}
            handleGoto={handleGoto}
          />
        )}
      </div>

      <AddModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setFormData(initialForm);
          setFormDataErr({});
        }}
        formData={formData}
        handleInputChange={handleInputChange}
        errors={formDataErr}
        onSave={handleSave}
      />
    </motion.div>
  );
};

export default MaintainLocations;
